import React, { useCallback, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'

import * as Analytics from 'analytics/analytics'
import { promotionClickEvent } from 'analytics/snowplow/events'
import config from 'constants/config'
import {
  CHARTER_BUNDLE_BANNER_CAMPAIGN_NAME,
  MALDIVES_BUNDLE_DEFAULT_NIGHTS,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
  MALDIVES_CHARTER_START_TARGET_DATE,
  MALDIVES_CHARTER_END_TARGET_DATE,
  NON_CHARTER_TO_CHARTER_OFFER_MAP,
} from 'constants/charterFlight'
import { FlightsClassTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import useOffer from 'hooks/Offers/useOffer'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import offerPageURL from 'lib/offer/offerPageURL'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'

import TextButton from 'LuxKit/Button/TextButton'
import Pill from 'LuxKit/Pill'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'

const defaultRooms: Array<App.Occupants> = [{ adults: 2 }]

interface Props {
  offer: App.HotelOffer
}

function CharterBundleBanner({ offer }: Props) {
  const intl = useIntl()
  const history = useHistory()
  const { currentRegionCode } = useContext(GeoContext)
  const formatCurrency = useCurrencyFormatter()

  const charterOfferId = NON_CHARTER_TO_CHARTER_OFFER_MAP[offer.id]

  const [charterOffer, charterOfferFetching] = useOffer<App.HotelOffer>(charterOfferId, {
    disabled: !charterOfferId,
  })
  const effectiveFareClass = useMemo(() => {
    const policy = getCharterFareClassPolicy({
      campaignTags: charterOffer?.campaignTags,
      occupancies: defaultRooms,
    })
    return policy.defaultFareClass ?? FlightsClassTypes.ECONOMY
  }, [charterOffer?.campaignTags])

  const selectedPackage = useMemo(() => {
    if (!charterOffer?.packages?.length) return charterOffer?.lowestPricePackage

    const matchingPackages = charterOffer.packages.filter(pkg => pkg.duration === MALDIVES_BUNDLE_DEFAULT_NIGHTS)

    if (matchingPackages.length === 0) {
      return charterOffer.lowestPricePackage
    }

    return matchingPackages.reduce((cheapest, pkg) => {
      return pkg.price < cheapest.price ? pkg : cheapest
    })
  }, [charterOffer])

  const [charterFlightPrice, flightPriceLoading] = useCharterFlightPrice({
    charterBundleRoute: 'melbourne-maldives',
    checkInDate: MALDIVES_CHARTER_START_TARGET_DATE,
    checkOutDate: MALDIVES_CHARTER_END_TARGET_DATE,
    occupancies: defaultRooms,
    fareClass: effectiveFareClass,
    disabled: !charterOfferId,
  })

  const memberPricePerPerson = useMemo(() => {
    if (!selectedPackage || !charterFlightPrice) {
      return undefined
    }

    const totalPassengers = defaultRooms.reduce((acc, room) => acc + (room.adults ?? 0) + (room.children ?? 0), 0)
    const hotelPrice = selectedPackage.memberPrice ?? selectedPackage.price
    const totalBundlePrice = hotelPrice + charterFlightPrice

    return totalPassengers > 0 ? Math.round(totalBundlePrice / totalPassengers) : Math.round(totalBundlePrice)
  }, [selectedPackage, charterFlightPrice])

  const isPriceLoading = flightPriceLoading || !selectedPackage || charterOfferFetching

  const charterOfferUrl = useMemo(() => {
    if (!charterOffer) return `/${currentRegionCode.toLowerCase()}/offer/${charterOfferId}`
    return `/${currentRegionCode.toLowerCase()}${offerPageURL(charterOffer)}`
  }, [charterOffer, charterOfferId, currentRegionCode])

  const handleViewBundleClick = useCallback(() => {
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: CHARTER_BUNDLE_BANNER_CAMPAIGN_NAME,
        promotionType: 'banner',
      },
    }))

    history.push(charterOfferUrl)
  }, [charterOfferUrl, history])

  const exclusiveBundleLabel = intl.formatMessage({
    id: 'Ra5guc5Qlv',
    defaultMessage: 'Exclusive bundle',
    description: 'Badge text for charter bundle offer on the offer page',
  })

  const viewBundleOfferLabel = intl.formatMessage({
    id: 'LlCfn97uuC',
    defaultMessage: 'View bundle offer',
    description: 'Button text to view the charter bundle offer details',
  })

  const flyDirectDescription = intl.formatMessage({
    id: '7JggSokaeG',
    defaultMessage: 'Fly direct from Melbourne to Malé, exclusively chartered for Luxury Escapes customers.',
    description: 'Description text for charter bundle banner on offer page',
  })

  if (!charterOfferId || currentRegionCode !== 'AU') {
    return null
  }

  return (
    <Sheet background="highlight-primary" borderRadius="M" padding="16 20">
      <Group direction="vertical" tabletDirection="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
        <Group direction="vertical" tabletDirection="horizontal" gap={2} tabletGap={12} tabletVerticalAlign="start">
          <CSSBreakpoint max="mobile">
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
              <Pill kind="primary" variant="success">{exclusiveBundleLabel}</Pill>
              <LinePlaneDepartureIcon size="L" />
            </Group>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <LinePlaneDepartureIcon size="L" />
          </CSSBreakpoint>
          <Group direction="vertical" gap={4}>
            <Group direction="vertical" tabletDirection="horizontal" gap={4} tabletVerticalAlign="center" wrap="wrap" desktopGap={4}>
              <CSSBreakpoint min="tablet">
                <Pill kind="primary" variant="success">{exclusiveBundleLabel}</Pill>
              </CSSBreakpoint>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'xghau5O/FL',
                  defaultMessage: 'Return flights + {nights}-night stay',
                  description: 'Charter bundle banner describing return flights plus hotel stay duration',
                }, { nights: MALDIVES_BUNDLE_DEFAULT_NIGHTS })}
              </BodyText>
              {(isPriceLoading || memberPricePerPerson) && (
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium" weight="bold" wrap="no-wrap">{intl.formatMessage({
                    id: 'hMCwaREzWW',
                    defaultMessage: 'from',
                    description: 'Price prefix label in charter bundle banner in the price breakdown',
                  })}
                  </BodyText>
                  {isPriceLoading && <PriceRowPriceLoadingSkeleton size="M" />}
                  {!isPriceLoading && memberPricePerPerson && (
                    <BodyText variant="large" weight="bold" colour="highlight-secondary" wrap="no-wrap">
                      {intl.formatMessage(
                        { id: '+O41Zydsp5', defaultMessage: '{price}/adult', description: 'Price per adult in charter bundle banner in the price breakdown' },
                        { price: formatCurrency(memberPricePerPerson) },
                      )}
                    </BodyText>
                  )}
                </Group>
              )}
            </Group>
            <BodyText variant="small" colour="neutral-two">
              {flyDirectDescription}
            </BodyText>
          </Group>
        </Group>

        <Group direction="horizontal" gap={8}>
          <TextButton
            kind="secondary"
            startIcon={<LinePhoneIcon />}
            href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
            rel="nofollow"
          >
            {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
          </TextButton>
          <TextButton
            kind="primary"
            onClick={handleViewBundleClick}
          >
            {viewBundleOfferLabel}
          </TextButton>
        </Group>
      </Group>
    </Sheet>
  )
}

export default React.memo(CharterBundleBanner)
